import React from 'react';
import moment from 'moment-timezone';

import { Modal, Button, Section, toast, DateTimeInput } from '@weezevent/nacre';

import useAppContext from '../../../../context/useAppContext';
import { useTranslation } from 'react-i18next';

import { ModalFooter } from '../../../modal-footer/modal-footer';
import Style from './style/schedule-modal.module.css';
import { TimeZoneSelect } from '../../../../timezones/TimeZoneSelect';

function useUpdateEventSchedule({ onUpdate = () => {}, onError = () => {} }) {
    const [loading, setLoading] = React.useState(false);
    const [{ event, reloadEvent, organization }] = useAppContext();

    const updateEvent = React.useCallback(
        value => {
            let newEvent = event.copy();
            newEvent.publication_date = value;

            setLoading(true);
            return newEvent
                .save({ organizationId: organization.id })
                .then(() => {
                    // Must return a promise
                    return reloadEvent(false);
                })
                .then(() => {
                    onUpdate();
                })
                .catch(e => {
                    onError();
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [event, onUpdate]
    );

    return [updateEvent, loading];
}

export const ScheduleModal = ({ open = false, onClose }) => {
    const { t, i18n } = useTranslation();
    const locale = i18n.language;

    const [{ event, event_timezone }] = useAppContext();
    const currentYear = new Date().getFullYear();

    const isPublicationScheduled = React.useMemo(() => {
        return event.isPublicationScheduled();
    }, [event]);

    const [updateEvent] = useUpdateEventSchedule({
        onUpdate: () => {
            if (isPublicationScheduled) {
                toast.success(t('ticket.menu.event.schedule_modal.edit_schedule_success'));
            } else {
                toast.success(t('ticket.menu.event.schedule_modal.schedule_success'));
            }
            onClose();
        },
        onError: () => {
            if (isPublicationScheduled) {
                return toast.error(t('ticket.menu.event.schedule_modal.edit_schedule_error'));
            }
            toast.error(t('ticket.menu.event.schedule_modal.schedule_error'));
        }
    });

    const [loading, setLoading] = React.useState(false);
    const [cancelLoading, setCancelLoading] = React.useState(false);

    const getClosestDate = React.useMemo(() => {
        const STEP = 5;
        let _date = moment.utc();
        let minutes = _date.minutes();
        minutes = Math.round(minutes / STEP) * STEP;
        return _date
            .add(1, 'hours')
            .set('minute', minutes)
            .set('second', 0)
            .format();
    }, []);

    const [date, setDate] = React.useState(event.publication_date || getClosestDate);

    const title = isPublicationScheduled ? t('ticket.menu.event.schedule_modal.title_edit') : t('ticket.menu.event.schedule_modal.title');
    const description = isPublicationScheduled ? t('ticket.menu.event.schedule_modal.description_edit') : t('ticket.menu.event.schedule_modal.description');

    const isDateInFuture = React.useMemo(() => {
        return (
            date >
            moment()
                .utc()
                .format()
        );
    }, [date]);

    return (
        <Modal size="large" allowExternalClosing={false} open={open} onClose={onClose} submitButton={false} cancelButton={false} title={title} subtitle={description}>
            <Section className={Style['date-input-timezone']}>
                <DateTimeInput
                    className={Style['schedule_modal__datetime']}
                    timeLabel={t('ticket.menu.event.schedule_modal.time_begin')}
                    dateLabel={t('ticket.menu.event.schedule_modal.date_begin')}
                    onChange={(date, dateObject) => {
                        setDate(date);
                    }}
                    value={date}
                    dateProps={{ minDate: moment(), minYear: currentYear - 7, maxYear: currentYear + 15 }}
                    locale={locale}
                    timezone={event_timezone}
                    error={!isDateInFuture && t('ticket.menu.event.schedule_modal.error')}
                />
                <TimeZoneSelect value={event_timezone} disabled className={Style['timezone']} />
            </Section>
            <ModalFooter>
                <Button onClick={onClose} label={t(`ticket.common.action.cancel`)} />
                {isPublicationScheduled && (
                    <Button
                        primary
                        inverted
                        label={t('ticket.menu.event.schedule_modal.cancel')}
                        isLoading={cancelLoading}
                        disabled={loading}
                        onClick={() => {
                            setCancelLoading(true);
                            updateEvent(null).finally(() => {
                                setCancelLoading(false);
                            });
                        }}
                    />
                )}
                <Button
                    primary
                    isLoading={loading}
                    disabled={cancelLoading || !isDateInFuture}
                    onClick={() => {
                        setLoading(true);
                        updateEvent(date).finally(() => {
                            setLoading(false);
                        });
                    }}
                    label={isPublicationScheduled ? t('ticket.common.action.edit') : t('ticket.menu.event.schedule_modal.accept')}
                />
            </ModalFooter>
        </Modal>
    );
};

ScheduleModal.defaultProps = {
    onClose: () => {}
};

export function CancelScheduleModal({ open, onClose }) {
    const { t } = useTranslation();

    const [updateEvent, loading] = useUpdateEventSchedule({
        onUpdate: () => {
            toast.success(t('ticket.menu.event.cancel_schedule_modal.success'));
            onClose();
        },
        onError: () => {
            toast.error(t('ticket.menu.event.cancel_schedule_modal.error'));
        }
    });

    return (
        <Modal
            allowExternalClosing={false}
            open={open}
            onClose={onClose}
            submitButton={<Button primary isLoading={loading} label={t('ticket.menu.event.cancel_schedule_modal.cta_validate')} onClick={() => updateEvent(null)} />}
            cancelButton={<Button onClick={onClose} label={t(`ticket.common.action.cancel`)} />}
            title={t('ticket.menu.event.cancel_schedule_modal.title')}
            subtitle={t('ticket.menu.event.cancel_schedule_modal.description')}
        />
    );
}

CancelScheduleModal.defaultProps = {
    onClose: () => {}
};
